// Blocklist ("cliente no deseado") helpers, shared by the customer/company edit
// pages, the client pickers and the reservation flow.
//
// The real guard lives in Postgres: `enforce_client_not_blocked()` runs BEFORE
// INSERT on `reservations` and refuses any blocked customer/company, so the bot
// and the website are covered too. Everything here is the UI half — it tells the
// user *why* before they hit the wall.

import supabase from "../supabase-client";

// Codes must stay in sync with the CHECK constraint on
// customers.block_reason / companies.block_reason and with the Spanish labels in
// public.client_block_reason_label().
export const BLOCK_REASONS = [
  { value: "abuso_de_confianza", label: "Abuso de confianza" },
  { value: "moroso", label: "Moroso" },
  { value: "sospechoso", label: "Sospechoso" },
  { value: "otro", label: "Otro" },
];

export const blockReasonLabel = (reason) =>
  BLOCK_REASONS.find((r) => r.value === reason)?.label ||
  "Sin motivo especificado";

const TABLE_FOR = { customer: "customers", company: "companies" };

const BLOCK_COLUMNS = "blocked, block_reason, block_notes, blocked_at, blocked_by";

// Reads just the blocklist columns for one client.
// Returns `{ blocked, block_reason, block_notes, blocked_at, blocked_by }`, or
// null when there's nothing to look up / the row is gone.
export async function fetchClientBlock(entityType, entityId) {
  const table = TABLE_FOR[entityType];
  if (!table || !entityId) return null;
  const { data, error } = await supabase
    .from(table)
    .select(BLOCK_COLUMNS)
    .eq("id", entityId)
    .maybeSingle();
  if (error) {
    console.error(error.message);
    return null;
  }
  return data || null;
}

// Human-readable reason, mirroring the wording of the DB exception so the user
// sees the same sentence whether we caught it up front or the trigger did.
export function blockMessage({ entityType, name, block_reason, block_notes }) {
  const who = entityType === "company" ? "Empresa bloqueada" : "Cliente bloqueado";
  const detail = (block_notes || "").trim();
  return (
    `${who}: ${name || "-"} — Motivo: ${blockReasonLabel(block_reason)}.` +
    (detail ? ` Detalle: ${detail}` : "")
  );
}

// Marks a client as blocked. `reason` must be one of BLOCK_REASONS.
export async function blockClient({
  entityType,
  entityId,
  reason,
  notes,
  userId,
}) {
  const table = TABLE_FOR[entityType];
  if (!table || !entityId) return { error: "Cliente inválido" };
  const patch = {
    blocked: true,
    block_reason: reason,
    block_notes: (notes || "").trim() || null,
    blocked_at: new Date().toISOString(),
    blocked_by: userId || null,
  };
  const { error } = await supabase.from(table).update(patch).eq("id", entityId);
  return error ? { error: error.message } : { data: patch };
}

// Clears the flag. The reason is wiped too — the DB CHECK only allows a reason
// without a block, not a block without a reason, and a stale motive on an active
// client reads as if they were still flagged.
export async function unblockClient({ entityType, entityId }) {
  const table = TABLE_FOR[entityType];
  if (!table || !entityId) return { error: "Cliente inválido" };
  const patch = {
    blocked: false,
    block_reason: null,
    block_notes: null,
    blocked_at: null,
    blocked_by: null,
  };
  const { error } = await supabase.from(table).update(patch).eq("id", entityId);
  return error ? { error: error.message } : { data: patch };
}
